/* eslint-disable max-classes-per-file */
import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { IUserStore } from '@stores/user';
import { RouteComponentProps, withRouter } from '@lib/router';
import { Path } from '@components/routes/constants';

interface IAuthenticateProps extends RouteComponentProps {
  userStore: IUserStore;
  children?: ReactNode;
}

// ComponentType (not a bare function type) so React.lazy() results (LazyExoticComponent) are
// accepted — TS 5 no longer treats those as assignable to a plain function-component type.
type ComposedComponent = React.ComponentType<IAuthenticateProps>;

/** Paths a user suspended in their selected organization is still allowed to visit. */
const SUSPENDED_USER_ALLOWED_PATHS: string[] = [Path.USER_SUSPENDED, Path.SELECT_ORGANIZATION];

abstract class AuthRouteGuard extends React.Component<IAuthenticateProps> {
  abstract render(): JSX.Element;

  protected abstract hasActiveSession(): boolean;

  /**
   * The path the current user must be redirected to, or `null` when the current
   * location is allowed.
   */
  private getRedirectPath(): string | null {
    const { location, userStore } = this.props;

    if (!this.hasActiveSession()) {
      return Path.LOGIN;
    }

    if (userStore.isUserSuspendedInSelectedOrganization) {
      return SUSPENDED_USER_ALLOWED_PATHS.includes(location.pathname) ? null : Path.USER_SUSPENDED;
    }

    return location.pathname === Path.USER_SUSPENDED ? Path.HOME : null;
  }

  // eslint-disable-next-line react/no-unused-class-component-methods -- called via super._render in subclasses
  protected _render(ComposedComponent: ComposedComponent) {
    const { location } = this.props;
    const redirectPath = this.getRedirectPath();

    if (redirectPath && redirectPath !== location.pathname) {
      // Redirect declaratively: <Navigate> performs its navigation in a passive
      // effect, i.e. after the data router has become active. An imperative
      // history.push() from a lifecycle method (componentDidMount) runs during
      // the first commit, before the router is active, and react-router v6/v7
      // silently drops it — so a logged-out deep-link load never reached /login.
      return <Navigate to={redirectPath} replace={redirectPath !== Path.LOGIN} />;
    }

    return <ComposedComponent {...this.props} />;
  }
}

/**
 * Route guard that requires User to be in a logged in state, eg have valid user store with idToken
 */
export const requiresAuth = (ComposedComponent: ComposedComponent): React.FC => {
  @inject('userStore')
  @observer
  class Authenticate extends AuthRouteGuard {
    protected hasActiveSession(): boolean {
      return this.props.userStore.loggedIn;
    }

    public render() {
      return super._render(ComposedComponent);
    }
  }
  // v6 renders route elements without injecting router props; withRouter supplies
  // history/location/match via hooks. userStore is provided by `@inject`.
  return withRouter(Authenticate) as unknown as React.FC;
};

/**
 * Route guard that requires User to either be logged in or have started an MFA flow,
 * such as MFA submission, or MFA set up
 */
export const requiresMfaSession = (ComposedComponent: ComposedComponent): React.FC => {
  @inject('userStore')
  @observer
  class Authenticate extends AuthRouteGuard {
    protected hasActiveSession(): boolean {
      const {
        userStore: { loggedIn, mfaData },
      } = this.props;
      return loggedIn || !!mfaData?.sessionToken;
    }

    public render() {
      return super._render(ComposedComponent);
    }
  }
  // v6 renders route elements without injecting router props; withRouter supplies
  // history/location/match via hooks. userStore is provided by `@inject`.
  return withRouter(Authenticate) as unknown as React.FC;
};
